import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { employeeAPI } from '../../services/api';
import { FileText, Building, Calendar, Star, ChevronRight, Loader2, Clock, CheckCircle, Flag, AlertCircle } from 'lucide-react';

const statusConfig = {
  acknowledged: { label: 'Verified', class: 'status-verified', icon: CheckCircle },
  pending_acknowledgment: { label: 'Pending Review', class: 'status-pending', icon: Clock },
  draft: { label: 'Employer Draft', class: 'status-draft', icon: FileText },
  flagged: { label: 'In Discussion', class: 'status-flagged', icon: Flag },
};

const Records = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, profileRes] = await Promise.all([
        employeeAPI.getRecords(),
        employeeAPI.getProfile()
      ]);
      setRecords(recordsRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="employee-records">
        {/* Header */}
        <div className="flex items-start justify-between animate-fade-in">
          <div className="flex items-center gap-4">
            {profile?.photo_url ? (
              <img src={profile.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[#E8EDF5]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#F5F8FF] border-2 border-[#E8EDF5] flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#5B6472]" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-[#0A0F1E]">
                {profile?.first_name || profile?.last_name 
                  ? `${profile.first_name} ${profile.last_name}`.trim()
                  : 'Your VETT Records'}
              </h1>
              {profile?.job_title && (
                <p className="text-[#5B6472]">{profile.job_title}</p>
              )}
              {profile?.open_to_opportunities && profile.open_to_opportunities !== 'not_looking' && (
                <span className={`inline-flex items-center mt-2 px-2 py-1 rounded-full text-xs ${
                  profile.open_to_opportunities === 'open' ? 'badge-open' : 'badge-selective'
                }`}>
                  {profile.open_to_opportunities === 'open' ? 'Open to opportunities' : 'Selectively open'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Records List */}
        <div className="space-y-4">
          {records.length === 0 ? (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
              <AlertCircle className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No records yet</h3>
              <p className="text-[#5B6472]">
                Your VETT Records will appear here once an employer submits one for you.
              </p>
            </Card>
          ) : (
            records.map((record, i) => {
              const status = statusConfig[record.status] || statusConfig.draft;
              return (
                <Link
                  key={record.id}
                  to={record.status === 'pending_acknowledgment' 
                    ? `/employee/records/${record.id}/review` 
                    : `/employee/records/${record.id}`}
                  className="block animate-fade-in"
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`record-card-${record.id}`}
                >
                  <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 hover:border-[#1B5AFF]/50 transition-colors group">
                    {record.status === 'pending_acknowledgment' && (
                      <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-amber-400" />
                        <span className="text-amber-400 text-sm">
                          Review this record and acknowledge when ready
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {record.employer_logo ? (
                          <img 
                            src={record.employer_logo} 
                            alt="" 
                            className="w-12 h-12 rounded-lg object-cover border border-[#E8EDF5]"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                            <Building className="w-5 h-5 text-[#5B6472]" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-[#0A0F1E] group-hover:text-[#1B5AFF] transition-colors">
                            {record.job_title}
                          </h3>
                          <p className="text-[#5B6472]">{record.employer_name || 'Unknown Company'}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-[#5B6472]">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {record.start_date} - {record.currently_employed ? 'Present' : record.end_date}
                            </span>
                            {record.rating && (
                              <span className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star 
                                    key={i} 
                                    className={`w-4 h-4 ${i < record.rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} 
                                  />
                                ))}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${status.class}`}>
                          <status.icon className="w-3.5 h-3.5" />
                          {status.label}
                        </span>
                        <ChevronRight className="w-5 h-5 text-[#5B6472] group-hover:text-[#1B5AFF] transition-colors" />
                      </div>
                    </div>
                    {record.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {record.skills.slice(0, 5).map((skill, i) => (
                          <span key={i} className="px-2 py-1 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-xs border border-[#E8EDF5]">
                            {skill}
                          </span>
                        ))}
                        {record.skills.length > 5 && (
                          <span className="px-2 py-1 rounded-full bg-[#F5F8FF] text-[#5B6472] text-xs border border-[#E8EDF5]">
                            +{record.skills.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                  </Card>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Records;
